import { z } from 'zod';
import { Entregaveis } from './entregaveis.js';
import {
  AjusteNaoUtil, AncoraBase, ClampDiaInvalido, EscopoCalendario, EscopoFeriado, Esfera, FonteDia, GatilhoObrigacao, ModoDia,
  Periodicidade, RegimeTributario, RequerEntrega, UF, UnidadeOffset,
} from './enums.js';
import { BoolQuery, CompetenciaChave, DataISO, Id } from './comum.js';

export const ParcelaRegra = z.object({
  numero: z.number().int().min(1).max(12),
  deslocamentoMesesAdicional: z.number().int().min(0).max(24).default(0),
  modoDiaOverride: ModoDia.nullable().default(null),
  diaOverride: z.number().int().min(1).max(31).nullable().default(null),
});
export type ParcelaRegra = z.infer<typeof ParcelaRegra>;

export const VarianteBody = z.object({
  prioridade: z.number().int().default(0),
  descricao: z.string().trim().max(120).nullable().default(null),
  ufs: z.array(UF).default([]),
  municipiosIbge: z.array(z.number().int()).default([]),
  regimes: z.array(RegimeTributario).default([]),
  anexosSimples: z.array(z.number().int().min(1).max(5)).default([]),
  ancora: AncoraBase.default('FIM_COMPETENCIA'),
  deslocamentoMeses: z.number().int().min(-12).max(24).default(1),
  mesFixo: z.number().int().min(1).max(12).nullable().default(null),
  anoOffset: z.number().int().min(-1).max(2).default(0),
  eventoTipo: z.string().trim().max(40).nullable().default(null),
  modoDia: ModoDia.default('DIA_DO_MES'),
  dia: z.number().int().min(1).max(31).nullable().default(null),
  fonteDia: FonteDia.default('REGRA'),
  parametroChave: z.string().trim().max(40).nullable().default(null),
  unidadeOffset: UnidadeOffset.nullable().default(null),
  sinalOffset: z.union([z.literal(1), z.literal(-1)]).default(1),
  ajuste: AjusteNaoUtil.default('POSTERGAR'),
  clamp: ClampDiaInvalido.default('ULTIMO_DIA_DO_MES'),
  calendarios: z.array(EscopoCalendario).min(1).default(['NACIONAL']),
  ufFixa: UF.nullable().default(null),
  municipioIbgeFixo: z.number().int().nullable().default(null),
  antecedenciaInternaDiasUteis: z.number().int().min(0).max(30).default(2),
  aberturaDiasAntes: z.number().int().min(0).max(120).default(20),
  parcelas: z.array(ParcelaRegra).max(12).default([]),
}).superRefine((v, ctx) => {
  if (v.ancora === 'DATA_FIXA_ANUAL' && !v.mesFixo) ctx.addIssue({ code: 'custom', path: ['mesFixo'], message: 'informe o mês fixo' });
  if (v.ancora === 'EVENTO' && v.modoDia !== 'OFFSET_DIAS') ctx.addIssue({ code: 'custom', path: ['modoDia'], message: 'regra por evento usa OFFSET_DIAS' });
  if (['DIA_DO_MES', 'N_ESIMO_DIA_UTIL', 'N_ESIMO_DIA_UTIL_REGRESSIVO', 'OFFSET_DIAS'].includes(v.modoDia) && v.fonteDia === 'REGRA' && v.dia == null) {
    ctx.addIssue({ code: 'custom', path: ['dia'], message: 'informe o dia' });
  }
  if (v.fonteDia === 'PARAMETRO_VINCULO' && !v.parametroChave) ctx.addIssue({ code: 'custom', path: ['parametroChave'], message: 'informe a chave do parâmetro' });
  if (v.modoDia === 'OFFSET_DIAS' && !v.unidadeOffset) ctx.addIssue({ code: 'custom', path: ['unidadeOffset'], message: 'informe corridos ou úteis' });
});
export type VarianteBody = z.infer<typeof VarianteBody>;
export const Variante = VarianteBody.safeExtend({ id: Id, versaoId: Id });
export type Variante = z.infer<typeof Variante>;

export const VersaoBody = z.object({
  vigenciaCompetenciaInicio: CompetenciaChave,
  vigenciaCompetenciaFim: CompetenciaChave.nullable().default(null),
  fundamentoLegal: z.string().trim().max(300).nullable().default(null),
  variantes: z.array(VarianteBody).min(1).max(30),
});
export type VersaoBody = z.infer<typeof VersaoBody>;
export const Versao = z.object({
  id: Id,
  vigenciaCompetenciaInicio: CompetenciaChave,
  vigenciaCompetenciaFim: CompetenciaChave.nullable(),
  fundamentoLegal: z.string().nullable(),
  variantes: z.array(Variante),
});
export type Versao = z.infer<typeof Versao>;

export const RegraBody = z.object({
  periodicidade: Periodicidade,
  competenciaOrdinaisValidos: z.array(z.number().int().min(1).max(12)).default([]),
  parcelasMax: z.number().int().min(1).max(12).default(1),
  versoes: z.array(VersaoBody).min(1).max(20),
});
export type RegraBody = z.infer<typeof RegraBody>;
export const Regra = z.object({
  id: Id,
  periodicidade: Periodicidade,
  competenciaOrdinaisValidos: z.array(z.number().int()),
  parcelasMax: z.number().int(),
  versoes: z.array(Versao),
});
export type Regra = z.infer<typeof Regra>;

export const ObrigacaoBody = z.object({
  codigo: z.string().trim().min(2).max(40).regex(/^[A-Z0-9_]+$/, 'use letras maiúsculas, números e _'),
  sigla: z.string().trim().min(1).max(16),
  nome: z.string().trim().min(3).max(160),
  descricao: z.string().trim().max(4000).nullable().default(null),
  esfera: Esfera,
  departamentoSugerido: z.string().trim().max(8).nullable().default(null),
  gatilho: GatilhoObrigacao.default('RECORRENTE'),
  requerEntrega: RequerEntrega.default('NENHUMA'),
  exigeRevisao: z.boolean().default(false),
  regimesAplicaveis: z.array(RegimeTributario).default([]),
  orgao: z.string().trim().max(80).nullable().default(null),
  linkOficial: z.string().trim().max(300).nullable().default(null),
  ativo: z.boolean().default(true),
  ordem: z.number().int().default(0),
  /** o que a obrigação gera (fixos e variáveis); ausente/nulo = não mexe (vale o padrão do catálogo); lista vazia = volta ao padrão */
  entregaveis: Entregaveis.nullable().optional(),
});
export type ObrigacaoBody = z.infer<typeof ObrigacaoBody>;

export const Obrigacao = ObrigacaoBody.extend({
  id: Id,
  /** null = catálogo global */
  escritorioId: Id.nullable(),
  origemId: Id.nullable(),
  periodicidade: Periodicidade.nullable(),
  /** resumo legível da regra vigente ("dia 20 do mês seguinte, posterga") */
  resumoRegra: z.string().nullable(),
  /** itens no checklist padrão deste escritório (0 = a tarefa nasce sem checklist) */
  checklistItens: z.number().int().default(0),
  /** existe checklist sugerido para esta obrigação (as criadas pelo escritório não têm) */
  temChecklistPadrao: z.boolean().default(false),
});
export type Obrigacao = z.infer<typeof Obrigacao>;

export const ChecklistsPadraoResultado = z.object({ /** obrigações que ganharam checklist agora */ preenchidas: z.number().int(), /** já tinham checklist: ficam como estão */ jaTinham: z.number().int(), /** criadas pelo escritório, sem sugestão no catálogo */ semPadrao: z.number().int() });
export type ChecklistsPadraoResultado = z.infer<typeof ChecklistsPadraoResultado>;

export const ObrigacaoDetalhe = Obrigacao.extend({
  regra: Regra.nullable(),
  prorrogacoes: z.array(z.object({
    id: Id, competenciaChave: CompetenciaChave.nullable(), parcelaNumero: z.number().int().nullable(),
    ufs: z.array(z.string()), municipiosIbge: z.array(z.number().int()), novoVencimento: DataISO, fundamentoLegal: z.string(),
  })),
});
export type ObrigacaoDetalhe = z.infer<typeof ObrigacaoDetalhe>;

export const ObrigacoesQuery = z.object({
  esfera: Esfera.optional(),
  regime: RegimeTributario.optional(),
  busca: z.string().trim().max(80).optional(),
  incluirInativas: BoolQuery.default(false),
});

export const ProrrogacaoBody = z.object({
  competenciaChave: CompetenciaChave.nullable().default(null),
  parcelaNumero: z.number().int().min(1).nullable().default(null),
  ufs: z.array(UF).default([]),
  municipiosIbge: z.array(z.number().int()).default([]),
  novoVencimento: DataISO,
  fundamentoLegal: z.string().trim().min(3).max(300),
});
export type ProrrogacaoBody = z.infer<typeof ProrrogacaoBody>;

// ---- feriados ----
export const FeriadoBody = z.object({
  escopo: EscopoFeriado,
  uf: UF.nullable().default(null),
  municipioIbge: z.number().int().nullable().default(null),
  municipioNome: z.string().trim().max(80).nullable().default(null),
  dia: z.number().int().min(1).max(31),
  mes: z.number().int().min(1).max(12),
  ano: z.number().int().min(2000).max(2100).nullable().default(null),
  nome: z.string().trim().min(2).max(120),
}).superRefine((f, ctx) => {
  if (f.escopo === 'UF' && !f.uf) ctx.addIssue({ code: 'custom', path: ['uf'], message: 'informe a UF' });
  if (f.escopo === 'MUNICIPIO' && (f.municipioIbge == null || !f.municipioNome)) ctx.addIssue({ code: 'custom', path: ['municipioIbge'], message: 'informe o município (código IBGE e nome)' });
});
export type FeriadoBody = z.infer<typeof FeriadoBody>;
export const Feriado = z.object({
  id: Id, escritorioId: Id.nullable(), escopo: EscopoFeriado, uf: z.string().nullable(), municipioIbge: z.number().int().nullable(),
  municipioNome: z.string().nullable(), dia: z.number().int(), mes: z.number().int(), ano: z.number().int().nullable(), nome: z.string(),
});
export type Feriado = z.infer<typeof Feriado>;

// ---- simulador ----
export const SimularBody = z.object({
  obrigacaoId: Id,
  ano: z.number().int().min(2020).max(2035),
  uf: UF.default('SP'),
  municipioIbge: z.number().int().nullable().default(null),
  regime: RegimeTributario.nullable().default(null),
  anexoSimples: z.number().int().min(1).max(5).nullable().default(null),
  parametros: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
  quotas: z.number().int().min(1).max(12).default(1),
  /** só para gatilho EVENTO: data do evento */
  dataEvento: DataISO.nullable().default(null),
});
export type SimularBody = z.infer<typeof SimularBody>;

export const SimulacaoLinha = z.object({
  competenciaChave: CompetenciaChave.nullable(),
  competenciaLabel: z.string(),
  parcelaNumero: z.number().int(),
  dataBase: DataISO,
  vencimentoLegal: DataISO,
  vencimentoProrrogado: DataISO.nullable(),
  vencimentoEfetivo: DataISO,
  diaSemana: z.string(),
  dataLimiteInterna: DataISO,
  dataAbertura: DataISO,
  ajuste: z.string().nullable(),
  origem: z.enum(['REGRA', 'PRORROGACAO']),
  varianteDescricao: z.string().nullable(),
  fundamentoLegal: z.string().nullable(),
});
export const SimulacaoResultado = z.object({
  obrigacao: z.object({ id: Id, sigla: z.string(), nome: z.string(), periodicidade: Periodicidade.nullable() }),
  calendarioHash: z.string(),
  feriadosConsiderados: z.array(z.object({ data: DataISO, nome: z.string() })),
  linhas: z.array(SimulacaoLinha),
  erro: z.string().nullable(),
});
export type SimulacaoResultado = z.infer<typeof SimulacaoResultado>;
